import React, {useEffect, useRef, useState} from 'react';
import {useDispatch} from 'react-redux';
import {Alert, Button, Loader, Table} from 'arag-ui-lib';
import ModalWrapper from '../../helpers/modalWrapper';
import {DOTS, DOWNLOAD, ERROR, MEDIUM, SIZE_LG} from '../../helpers/constants';
import {_getFileFromBase64} from '../../helpers/functions';
import {clearGplPolicyDocumentsState, fetchGplPolicyDocumentFile, fetchGplPolicyDocuments} from '../../actions/gplPolicyDocuments';
import '../../style/table.css';
import '../../style/gplWordingModal.css';

const WORDING = 'wording';
const IPID = 'ipid';

// Groups the flat document list into the reference layout: one table per groupName, wrapped into a
// visually separated block per jurisdiction. Both keys come from the API rather than being parsed
// out of the slug or the heading text, and the manifest's declaration order is the display order,
// so this only has to preserve first-appearance order - never sort.
export const groupDocuments = (documents) => {
    const blocks = [];

    (documents || []).forEach((document) => {
        let block = blocks.length ? blocks[blocks.length - 1] : null;
        if (!block || block.jurisdiction !== document.jurisdiction) {
            block = {jurisdiction: document.jurisdiction, groups: []};
            blocks.push(block);
        }

        let group = block.groups.length ? block.groups[block.groups.length - 1] : null;
        if (!group || group.groupName !== document.groupName) {
            group = {groupName: document.groupName, documents: []};
            block.groups.push(group);
        }

        group.documents.push(document);
    });

    return blocks;
};

// WI 99459: GPL policies take their wording and IPID from this modal rather than from the
// per-policy download. Bytes are fetched one document at a time on click - the list endpoint
// deliberately carries metadata only.
//
// Review: this used to call fetch() directly. It now dispatches through the HTTP cycle like every
// other request in the app, and the download itself fires from an effect watching the store's
// fetchingDocument flag go true -> false - the same shape applicationPolicyHOC.js uses for its
// document downloads. Going through the driver also means the central 401 handling applies rather
// than being re-implemented here.
const GplWordingModal = (props) => {
    const {isOpen, onClose, documents, fetching, failed,
        documentToDownload, fetchingDocument, failedToGetDocument} = props;
    const dispatch = useDispatch();
    const [downloadingKey, setDownloadingKey] = useState(null);
    const prevFetchingDocumentRef = useRef(false);
    const prevIsOpenRef = useRef(false);

    // WI 99459 fix wave finding 4: ModalWrapper keeps this component mounted while closed
    // (Modal fadeIn={isOpen}), so state from a previous open must be cleared explicitly rather
    // than relying on unmount, otherwise a stale error/spinner reappears on the next open.
    //
    // PR 16621 review: the first version cleared only downloadingKey, which is local state. The
    // two flags that actually render the error UI - failed and failedToGetDocument - live in the
    // reducer, which resets on LOCATION_CHANGE / LOGOUT only. Neither fires on close, so a failed
    // download left its red Alert sitting in the modal on every subsequent open until the user
    // navigated away from the policy.
    useEffect(() => {
        if (!isOpen) {
            setDownloadingKey(null);
            dispatch(clearGplPolicyDocumentsState());
        }
    }, [isOpen, dispatch]);

    // The list fetch lives here rather than in policyTab's mount effect, for two reasons.
    //
    // Retry: policyTab's effect was keyed on [isGpl, dispatch], and isGpl cannot change while the
    // tab is mounted, so it fired exactly once. A single failed list request therefore left
    // failed: true permanently - reopening the modal re-rendered the same banner over an empty
    // table with no way back short of a page navigation. Keyed on the open transition, every
    // reopen is a retry.
    //
    // Waste: the request no longer fires for GPL policies whose modal is never opened, which is
    // most of them.
    //
    // Fires at most once per open (the ref makes it an edge, not a level), so a legitimately empty
    // list cannot spin up a re-dispatch loop the way a documents.length check alone would. Skipped
    // when documents are already loaded, so reopening does not flash a spinner over good data.
    useEffect(() => {
        const justOpened = isOpen && !prevIsOpenRef.current;
        prevIsOpenRef.current = isOpen;

        if (justOpened && !fetching && (failed || !documents?.length)) {
            dispatch(fetchGplPolicyDocuments());
        }
    }, [isOpen, fetching, failed, documents, dispatch]);

    // Fire the actual save on the true -> false transition rather than on documentToDownload
    // changing, so re-downloading the same document twice still triggers a save.
    useEffect(() => {
        const prevFetching = prevFetchingDocumentRef.current;
        prevFetchingDocumentRef.current = fetchingDocument;

        if (prevFetching && !fetchingDocument) {
            setDownloadingKey(null);
            if (!failedToGetDocument && documentToDownload) {
                _getFileFromBase64(documentToDownload);
            }
        }
    }, [fetchingDocument, failedToGetDocument, documentToDownload]);

    const download = (slug, kind) => {
        setDownloadingKey(`${slug}-${kind}`);
        dispatch(fetchGplPolicyDocumentFile({slug, kind}));
    };

    // Version-skew tolerance. The API and the UI ship as separate artifacts, so a UI carrying the
    // grouped layout can run against an API that still serves caseTypes as the old joined string.
    // Render that as a single bullet - the previous behaviour - rather than letting `.map` throw
    // and take the whole modal down. Deliberately does not split on a separator: guessing one
    // would mangle any case type that legitimately contains it.
    const toCaseTypeList = (caseTypes) => {
        if (Array.isArray(caseTypes)) {
            return caseTypes;
        }
        return typeof caseTypes === 'string' && caseTypes.trim() ? [caseTypes] : [];
    };

    const renderCaseTypes = (document) => (
        <ul className="gplWordingModal-caseTypes">
            {toCaseTypeList(document.caseTypes).map(caseType => (
                <li key={caseType}>{caseType}</li>
            ))}
        </ul>
    );

    // Review: `disabled` is keyed off the store-wide fetchingDocument, NOT off downloadingKey.
    // The store has ONE fetchingDocument flag for all 22 buttons (11 documents, each with a
    // wording and an IPID), and the save fires on its true -> false transition, so a second click
    // while a first request is in flight used to be lost silently: the first response cleared the
    // flag, saved its own file and re-enabled the second button, and the second response then
    // found prevFetching already false and never fired. One file arrived, no error, button looked
    // ready. The documents are 1.2-2.2 MB, so that window is wide enough to hit in normal use.
    //
    // Disabling every button while any fetch is in flight makes the single flag accurate.
    // downloadingKey now only marks WHICH row is busy. Allowing real concurrency instead would
    // need a request id carried through the action and back so the effect could match response
    // to request - not worth it for a modal where one download at a time is the normal case.
    const renderDocumentCell = (document, kind, name) => {
        const key = `${document.slug}-${kind}`;
        return (
            <div className="gplWordingModal-documentCell">
                <span>{name}</span>
                <Button
                    variant={DOWNLOAD}
                    onClick={() => download(document.slug, kind)}
                    disabled={fetchingDocument}
                    active={downloadingKey === key}
                    buttonTestId={`gpl-${kind}-download-${document.slug}`}
                />
            </div>
        );
    };

    const columns = [
        {
            propertyName: 'caseTypes',
            header: 'Case Type(s)',
            style: {width: '30%'},
            formatter: (document) => renderCaseTypes(document)
        },
        {
            propertyName: 'wordingName',
            header: 'Wording Document',
            style: {width: '35%'},
            formatter: (document) => renderDocumentCell(document, WORDING, document.wordingName)
        },
        {
            propertyName: 'ipidName',
            header: 'IPID Document',
            style: {width: '35%'},
            formatter: (document) => renderDocumentCell(document, IPID, document.ipidName)
        }
    ];

    const blocks = groupDocuments(documents);

    return (
        <ModalWrapper
            isOpen={isOpen}
            onClose={onClose}
            size={SIZE_LG}
            title="Policy Wordings & IPIDs"
            modalTestId="gpl-wording-modal"
            buttonTestId="gpl-wording-modal-close-button"
        >
            {/* AC7: six tables overflow most screens, so the body of the modal scrolls.
                ModalWrapper does not forward ModalBody's own "contained" scroll prop, so the
                scroll container is applied here instead. */}
            <div className="gplWordingModal-scroll">
                {fetching && <Loader variant={DOTS} dotSize={MEDIUM}/>}
                {failed && (
                    <Alert
                        label="Failed to load the policy wording and IPID documents. Try again later."
                        variant={ERROR}
                    />
                )}
                {failedToGetDocument && (
                    <Alert
                        label="Failed to download the document. Try again later."
                        variant={ERROR}
                    />
                )}
                {!fetching && !failed && (
                    <div>
                        <p className="gplWordingModal-intro">
                            Please use the table below to determine and download your relevant
                            Policy Wording &amp; IPID for this policy.
                        </p>
                        {blocks.map(block => (
                            <div className="gplWordingModal-block" key={block.jurisdiction}>
                                {block.groups.map(group => (
                                    <div className="gplWordingModal-group" key={group.groupName}>
                                        {/* Suppressed rather than rendered empty when an older API
                                            serves no groupName - see toCaseTypeList on skew. */}
                                        {group.groupName && (
                                            <div
                                                className="gplWordingModal-groupHeading"
                                                data-testid={`gpl-wording-group-${group.groupName}`}
                                            >
                                                {group.groupName}
                                            </div>
                                        )}
                                        <div className="table-container">
                                            <Table
                                                columns={columns}
                                                data={group.documents}
                                                tableWrapperTestId={`gpl-wording-table-${group.groupName}`}
                                            />
                                        </div>
                                    </div>
                                ))}
                            </div>
                        ))}
                    </div>
                )}
            </div>
        </ModalWrapper>
    );
};

export default GplWordingModal;
